import { useEffect, useState } from "react";
import { Mail, Send, ShieldCheck, AlertCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader } from "../ui";

export default function AdminConfiguracoes() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    smtp_from: "",
    use_tls: true,
    enabled: true,
  });
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const load = () =>
    api
      .get("/admin/settings/email")
      .then((r) => {
        setCfg(r.data);
        setForm((f) => ({
          ...f,
          smtp_host: r.data.smtp_host || "",
          smtp_port: r.data.smtp_port || 587,
          smtp_user: r.data.smtp_user || "",
          smtp_password: "",
          smtp_from: r.data.smtp_from || "",
          use_tls: r.data.use_tls !== false,
          enabled: r.data.enabled !== false,
        }));
      })
      .catch(() => toast.error("Não foi possível carregar as configurações"));

  useEffect(() => {
    load();
  }, []);

  const upd = (k) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, smtp_port: Number(form.smtp_port) || 587 };
      // Empty password = do not change
      if (!payload.smtp_password) delete payload.smtp_password;
      await api.put("/admin/settings/email", payload);
      toast.success("Configurações de e-mail salvas");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const [lastTest, setLastTest] = useState(null);

  const sendTest = async () => {
    if (!testEmail.includes("@")) {
      toast.error("Informe um e-mail válido");
      return;
    }
    setSendingTest(true);
    try {
      const res = await api.post("/admin/settings/email/test", { to: testEmail.trim() });
      const d = res.data || {};
      setLastTest({
        to: testEmail.trim(),
        message_id: d.message_id,
        envelope_from: d.envelope_from,
        quit_response: d.quit_response,
        refused_recipients: d.refused_recipients,
      });
      const refusedCount = Object.keys(d.refused_recipients || {}).length;
      if (refusedCount > 0) {
        toast.warning(
          `SMTP aceitou a conexão mas rejeitou ${refusedCount} destinatário(s). Veja o transcript.`
        );
      } else {
        toast.success(
          `E-mail entregue ao servidor SMTP. Veja o transcript com Message-ID.`
        );
      }
    } catch (e) {
      const errText =
        formatApiErrorDetail(e.response?.data?.detail) || "Erro ao enviar";
      toast.error(errText);
      setLastTest({
        to: testEmail.trim(),
        error: errText,
      });
    } finally {
      setSendingTest(false);
    }
  };

  if (!cfg)
    return (
      <div className="text-sm text-slate-500">Carregando configurações...</div>
    );

  return (
    <div data-testid="admin-configuracoes">
      <PageHeader
        title="Configuração de E-mail"
        subtitle="Defina o servidor SMTP usado para enviar notificações automáticas aos associados (aprovação de cadastro, novos boletos, comunicados)."
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-6 lg:col-span-2">
          <form data-testid="email-settings-form" onSubmit={save} className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Servidor SMTP *</Label>
              <Input
                testid="smtp-host"
                value={form.smtp_host}
                onChange={upd("smtp_host")}
                placeholder="smtp.ablemp.org.br"
                required
              />
            </div>
            <div>
              <Label>Porta *</Label>
              <Input
                testid="smtp-port"
                type="number"
                value={form.smtp_port}
                onChange={upd("smtp_port")}
                min={1}
                max={65535}
                required
              />
              <div className="text-xs text-slate-500 mt-1">
                Padrão: 587 (STARTTLS) ou 465 (SSL).
              </div>
            </div>
            <div>
              <Label>Conexão segura</Label>
              <label className="mt-2 inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  data-testid="smtp-tls"
                  type="checkbox"
                  checked={form.use_tls}
                  onChange={upd("use_tls")}
                />
                Usar STARTTLS / TLS
              </label>
            </div>
            <div>
              <Label>Usuário (login) *</Label>
              <Input
                testid="smtp-user"
                value={form.smtp_user}
                onChange={upd("smtp_user")}
                placeholder="ablemp@ablemp.org.br"
                required
              />
            </div>
            <div>
              <Label>
                Senha {cfg.smtp_password_set ? "(deixe em branco para manter)" : "*"}
              </Label>
              <Input
                testid="smtp-password"
                type="password"
                value={form.smtp_password}
                onChange={upd("smtp_password")}
                placeholder={cfg.smtp_password_set ? "••••••••" : "Senha do e-mail"}
                autoComplete="new-password"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Remetente exibido</Label>
              <Input
                testid="smtp-from"
                value={form.smtp_from}
                onChange={upd("smtp_from")}
                placeholder='ABLEMP <ablemp@ablemp.org.br>'
              />
              <div className="text-xs text-slate-500 mt-1">
                Como o nome aparece para os destinatários.
              </div>
            </div>
            <div className="md:col-span-2 border-t border-slate-200 pt-4 mt-2 flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  data-testid="smtp-enabled"
                  type="checkbox"
                  checked={form.enabled}
                  onChange={upd("enabled")}
                />
                Envio de e-mails ativado
              </label>
              <button
                data-testid="smtp-save"
                disabled={busy}
                className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
              >
                <Save size={14} />
                {busy ? "Salvando..." : "Salvar configurações"}
              </button>
            </div>
          </form>
        </Card>

        <div className="space-y-5">
          <Card className="p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-emerald-700">
              <ShieldCheck size={12} /> Notificações automáticas
            </div>
            <h3 className="mt-2 font-serif-display text-xl">Quando os e-mails são enviados?</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2">
                <span className="text-[#FEDF00]">●</span>
                Aprovação de cadastro de um associado.
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#FEDF00]">●</span>
                Envio de um novo arquivo (boleto, comunicado, contrato…) para os destinatários.
              </li>
            </ul>
            <p className="mt-3 text-xs text-slate-500">
              Falhas de envio não bloqueiam a ação — você sempre pode reenviar o
              documento depois.
            </p>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
              <Mail size={12} /> Enviar e-mail de teste
            </div>
            <h3 className="mt-2 font-serif-display text-xl">
              Confirme se está tudo certo
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Use as configurações salvas para enviar um e-mail de teste.
            </p>
            <div className="mt-3 flex gap-2">
              <input
                data-testid="test-email-input"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="seu@email.com.br"
                className="flex-1 border border-slate-300 px-3 py-2.5 rounded-sm text-sm"
              />
              <button
                data-testid="test-email-btn"
                onClick={sendTest}
                disabled={sendingTest}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
              >
                <Send size={14} />
                {sendingTest ? "Enviando..." : "Testar"}
              </button>
            </div>
            <div className="mt-3 text-xs text-amber-700 inline-flex items-start gap-2">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              Salve as alterações antes de testar.
            </div>
            {lastTest && (
              <div
                data-testid="test-email-transcript"
                className={`mt-4 border rounded-sm p-3 text-[11px] space-y-1 font-mono ${
                  lastTest.error
                    ? "border-rose-200 bg-rose-50 text-rose-900"
                    : "border-slate-200 bg-slate-50 text-slate-700"
                }`}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-sans">
                  Transcript SMTP · último teste
                </div>
                <div>
                  <span className="text-slate-500">Destinatário:</span>{" "}
                  {lastTest.to}
                </div>
                {lastTest.error ? (
                  <div className="break-all">
                    <span className="text-slate-500">Erro do servidor SMTP:</span>{" "}
                    <strong>{lastTest.error}</strong>
                  </div>
                ) : (
                  <>
                    <div>
                      <span className="text-slate-500">Envelope From:</span>{" "}
                      {lastTest.envelope_from || "—"}
                    </div>
                    <div className="break-all">
                      <span className="text-slate-500">Message-ID:</span>{" "}
                      {lastTest.message_id || "—"}
                    </div>
                    <div>
                      <span className="text-slate-500">Resposta ao QUIT:</span>{" "}
                      {lastTest.quit_response
                        ? `${lastTest.quit_response[0]} — ${lastTest.quit_response[1]}`
                        : "—"}
                    </div>
                    <div>
                      <span className="text-slate-500">Rejeitados:</span>{" "}
                      {Object.keys(lastTest.refused_recipients || {}).length === 0
                        ? "nenhum"
                        : JSON.stringify(lastTest.refused_recipients)}
                    </div>
                  </>
                )}
                <div className="pt-2 mt-2 border-t border-slate-200 font-sans text-[11px] text-slate-600">
                  {lastTest.error && lastTest.error.includes("535") ? (
                    <>
                      <strong>Autenticação SMTP recusada pelo servidor.</strong>{" "}
                      A senha do usuário{" "}
                      {lastTest.envelope_from || "ablemp@ablemp.org.br"} não é
                      mais aceita pelo servidor{" "}
                      <em>email-ssl.com.br</em>. Acesse o painel da KingHost,
                      redefina a senha do e-mail e atualize esta configuração.
                    </>
                  ) : (
                    <>
                      Se nada chegou apesar deste transcript:
                      <ul className="list-disc ml-4 mt-1 space-y-0.5">
                        <li>
                          Verifique a caixa de <strong>saída</strong> e{" "}
                          <strong>bounces/devoluções</strong> da conta{" "}
                          {lastTest.envelope_from || "ablemp@ablemp.org.br"} — o
                          servidor SMTP pode ter aceito e depois falhado ao
                          entregar.
                        </li>
                        <li>
                          Configure <strong>SPF / DKIM / DMARC</strong> para o
                          domínio ablemp.org.br (necessário para não cair em
                          spam do Gmail/Outlook).
                        </li>
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      <div className="mt-10">
        <AdminsSection />
      </div>
    </div>
  );
}

function AdminsSection() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin",
  });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/admin/admins")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erro ao carregar administradores."));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.toLowerCase().endsWith("@ablemp.org.br")) {
      toast.error("O e-mail deve terminar com @ablemp.org.br");
      return;
    }
    if (form.password.length < 8) {
      toast.error("A senha deve ter ao menos 8 caracteres.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/admins", form);
      toast.success("Administrador criado!");
      setForm({ name: "", email: "", password: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (a) => {
    setEditing(a);
    setEditForm({
      name: a.name,
      email: a.email,
      role: a.role || "admin",
      password: "",
      status: a.status || "ativo",
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const patch = {};
    if (editForm.name !== editing.name) patch.name = editForm.name;
    if ((editForm.email || "").trim().toLowerCase() !== (editing.email || "").toLowerCase()) {
      const newEmail = (editForm.email || "").trim().toLowerCase();
      if (!newEmail.endsWith("@ablemp.org.br")) {
        toast.error("O e-mail deve terminar com @ablemp.org.br");
        return;
      }
      patch.email = newEmail;
    }
    if (editForm.role && editForm.role !== editing.role) patch.role = editForm.role;
    if (editForm.status !== editing.status) patch.status = editForm.status;
    if (editForm.password) patch.password = editForm.password;
    if (Object.keys(patch).length === 0) {
      toast.message("Nada a alterar.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/admins/${editing.id}`, patch);
      toast.success("Administrador atualizado.");
      setEditing(null);
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Excluir ${a.name}? Esta ação não pode ser desfeita.`))
      return;
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success("Administrador excluído.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Administradores"
        subtitle="Crie e gerencie contas com acesso total ao Portal. O e-mail deve obrigatoriamente terminar em @ablemp.org.br. A senha pode ser alterada a qualquer momento."
        actions={
          <button
            data-testid="new-admin-btn"
            onClick={() => {
              setOpen((v) => !v);
              setForm({ name: "", email: "", password: "", role: "admin" });
            }}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            <ShieldCheck size={14} /> Novo administrador
          </button>
        }
      />

      {open && (
        <Card className="p-5 mb-5">
          <form
            data-testid="new-admin-form"
            onSubmit={submit}
            className="grid md:grid-cols-2 gap-3"
          >
            <div>
              <Label>Nome completo *</Label>
              <Input
                testid="admin-name"
                required
                minLength={2}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>E-mail @ablemp.org.br *</Label>
              <Input
                testid="admin-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="nome@ablemp.org.br"
              />
            </div>
            <div>
              <Label>Função *</Label>
              <select
                data-testid="admin-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
              >
                <option value="admin">Administrador</option>
                <option value="diretor_financeiro">Diretor Financeiro</option>
                <option value="presidente">Presidente</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Todos têm os mesmos poderes administrativos. A função aparece
                como assinatura em recibos e no cabeçalho do portal.
              </p>
            </div>
            <div className="md:col-span-2">
              <Label>Senha inicial (mín. 8 caracteres) *</Label>
              <Input
                testid="admin-password"
                type="text"
                minLength={8}
                required
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
              />
              <p className="text-xs text-slate-500 mt-1">
                A senha será enviada por e-mail. O novo administrador pode
                alterá-la depois.
              </p>
            </div>
            <div className="md:col-span-2 flex gap-2">
               <button
                  disabled={busy}
                  data-testid="admin-submit"
                  className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
                >
                  {busy ? "Salvando..." : "Criar administrador"}
                </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-5 py-2.5 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
              >
                Cancelar
              </button>
            </div>
          </form>
        </Card>
      )}

      {items === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left p-3">Nome</th>
                  <th className="text-left p-3">E-mail</th>
                  <th className="text-left p-3">Função</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`admin-row-${a.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="p-3 font-medium">{a.name}</td>
                    <td className="p-3 text-slate-600">{a.email}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 border text-[11px] rounded-sm ${
                        a.role === "diretor_financeiro"
                          ? "border-[#FEDF00] bg-[#FEDF00]/15 text-[#0A0F14]"
                          : a.role === "presidente"
                          ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                          : "border-[#002776]/30 bg-[#002776]/8 text-[#002776]"
                      }`}>
                        {a.role === "diretor_financeiro"
                          ? "Diretor Financeiro"
                          : a.role === "presidente"
                          ? "Presidente"
                          : "Administrador"}
                      </span>
                    </td>
                    <td className="p-3">
                      {a.status === "inativo" ? (
                        <span className="px-2 py-0.5 border border-rose-200 bg-rose-50 text-rose-700 text-[11px] rounded-sm">
                          Inativo
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] rounded-sm">
                          Ativo
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          data-testid={`admin-edit-${a.id}`}
                          onClick={() => openEdit(a)}
                          className="px-3 py-1 border border-slate-300 text-[10px] uppercase tracking-wide hover:border-[#002776]"
                        >
                          Editar
                        </button>
                        <button
                          data-testid={`admin-delete-${a.id}`}
                          onClick={() => remove(a)}
                          className="px-3 py-1 border border-rose-300 text-rose-700 text-[10px] uppercase tracking-wide hover:bg-rose-50"
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing && editForm && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4">
          <div className="bg-white rounded-md w-full max-w-md p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                  Editar administrador
                </div>
                <h3 className="mt-1 font-serif-display text-2xl">
                  {editing.email}
                </h3>
              </div>
              <button
                onClick={() => {
                  setEditing(null);
                  setEditForm(null);
                }}
                className="text-slate-500 hover:text-slate-900 text-2xl"
              >
                ×
              </button>
            </div>
            <form
              data-testid="admin-edit-form"
              onSubmit={submitEdit}
              className="space-y-3"
            >
              <div>
                <Label>Nome</Label>
                <Input
                  testid="admin-edit-name"
                  required
                  minLength={2}
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input
                  testid="admin-edit-email"
                  type="email"
                  required
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                />
                <p className="text-xs text-slate-500 mt-1">
                  Deve terminar com <strong>@ablemp.org.br</strong>. Alterar o
                  e-mail muda o login desse administrador.
                </p>
              </div>
              <div>
                <Label>Função</Label>
                <select
                  data-testid="admin-edit-role"
                  value={editForm.role}
                  onChange={(e) =>
                    setEditForm({ ...editForm, role: e.target.value })
                  }
                  className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
                >
                  <option value="admin">Administrador</option>
                  <option value="diretor_financeiro">Diretor Financeiro</option>
                  <option value="presidente">Presidente</option>
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <select
                  data-testid="admin-edit-status"
                  value={editForm.status}
                  onChange={(e) =>
                    setEditForm({ ...editForm, status: e.target.value })
                  }
                  className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>
              <div>
                <Label>Nova senha (opcional, mín. 8)</Label>
                <Input
                  testid="admin-edit-password"
                  type="text"
                  minLength={8}
                  value={editForm.password}
                  onChange={(e) =>
                    setEditForm({ ...editForm, password: e.target.value })
                  }
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setEditForm(null);
                  }}
                  className="px-4 py-2 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
                >
                  Cancelar
                </button>
                <button
                  disabled={busy}
                  data-testid="admin-edit-submit"
                  className="px-5 py-2 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
    {children}
  </label>
);

const Input = ({ testid, ...rest }) => (
  <input
    data-testid={testid}
    {...rest}
    className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002776]/30 focus:border-[#002776]"
  />
);
